import { useTranslation } from 'next-i18next';
import React, { useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import { shallowEqual } from 'react-redux';
import { useMobileApp } from '../../../../../../../hooks/useMobileApp';
import { fetchAndSetLivestreams, setSelectedPresentationLivestreamSlot } from '../../../../../../../reduxToolkit/slices/commonSlice';
import { useSelector, useDispatch } from '../../../../../../../reduxToolkit/typedRedux';
import { createDisplayLink, getFingerprint } from '../../../../../../../services/helper';
import { trackEvent } from '../../../../../../../services/services';
import VyModal from '../../../../../../common/VyModal';
import { PresentationLivestreamChat } from './PresentationLivestreamChat';

export const PresentationLivestreamModal = () => {
  const selectedPresentationLivestreamSlot = useSelector((state) => state.common.selectedPresentationLivestreamSlot);
  const stream = selectedPresentationLivestreamSlot?.meetingroom?.liveStream as any;
  // The following line contains that live updated status of the stream
  const currentStreamWithPusherStatus = useSelector((state) => state.common.livestreams?.find((ls) => ls._id === stream?._id), shallowEqual);
  const isMobilePreview = useSelector((state) => state.common.isMobilePreview);
  const event = useSelector((state) => state.common.event);
  const meetingChannel = useSelector((state) => state.common.pusherChannels.meetingChannel);

  const playerRef = useRef<ReactPlayer>(null);
  /**
   * We should add a presence channel here to see how many people have the modal open to give
   * the speakers in the room an indication of how many people are watching
   * https://github.com/mmmaxi/vystem-platform/issues/9970
   */
  const dispatch = useDispatch();
  const { isAppView } = useMobileApp();
  const { t } = useTranslation('event');

  const [hideStatusMessage, setHideStatusMessage] = useState(false);

  async function track(eventName: string) {
    try {
      const visitorId = await getFingerprint();
      await trackEvent(eventName, { streamId: stream?._id, visitorId, slotId: selectedPresentationLivestreamSlot?._id }, event?._id);
    } catch (err) {
      console.error(err);
    }
  }

  useEffect(() => {
    setHideStatusMessage(!!currentStreamWithPusherStatus?.isBroadcasting);
  }, [currentStreamWithPusherStatus?.isBroadcasting]);

  useEffect(() => {
    if (!meetingChannel || !selectedPresentationLivestreamSlot) {
      return;
    }

    if (meetingChannel.name !== `meeting-${selectedPresentationLivestreamSlot.meetingroom._id}`) {
      meetingChannel?.unsubscribe();
    }
  }, [meetingChannel, selectedPresentationLivestreamSlot]);

  useEffect(() => {
    if (!currentStreamWithPusherStatus && selectedPresentationLivestreamSlot && event) {
      dispatch(fetchAndSetLivestreams(event._id));
    }
  }, [selectedPresentationLivestreamSlot, currentStreamWithPusherStatus]);

  if (!selectedPresentationLivestreamSlot || !currentStreamWithPusherStatus) return null;
  if (!window) return null;

  const getStreamStatusMessage = (status?: string) => {
    if ((status === 'live' && !currentStreamWithPusherStatus.isBroadcasting) || status === 'upcoming') {
      return t('presentation.stream-not-live');
    } else if (status === 'old') {
      return t('presentation-over');
    }
  };

  const subTitle = (
    <>
      {selectedPresentationLivestreamSlot.meetingroom.booth?.name}{' '}
      {selectedPresentationLivestreamSlot.meetingroom.contactperson &&
        `${selectedPresentationLivestreamSlot.meetingroom.booth?.name ? '| ' : ''} ${selectedPresentationLivestreamSlot.meetingroom.contactperson}`}
    </>
  );

  return (
    <VyModal
      disablePortal={isMobilePreview}
      open={!!selectedPresentationLivestreamSlot}
      onClose={() => dispatch(setSelectedPresentationLivestreamSlot(undefined))}
      style={{ width: '90vw', maxWidth: '100%', height: '80vh' }}
      maxHeight={'100%'}
      disableDefaultStyling>
      {() => (
        <div className="flex flex-col w-full h-full gap-4 bg-white rounded max-h-fit sm:flex-row">
          <div className="relative flex flex-col w-full gap-2 sm:w-2/3 max-h-[66%] sm:max-h-full justify-between h-[80vh]">
            <div className="flex flex-col w-full">
              <p className="text-xs">{subTitle}</p>
              <p className="text-base font-bold">{selectedPresentationLivestreamSlot.meetingroom.name}</p>
              {!hideStatusMessage && getStreamStatusMessage(selectedPresentationLivestreamSlot.status) && (
                <p className="font-bold text-red-600">{getStreamStatusMessage(selectedPresentationLivestreamSlot.status)}</p>
              )}
            </div>
            <ReactPlayer
              key={`livestream-${currentStreamWithPusherStatus?._id}-${currentStreamWithPusherStatus.isBroadcasting}`}
              ref={playerRef}
              playing={currentStreamWithPusherStatus.isBroadcasting}
              playsinline={isAppView()}
              url={currentStreamWithPusherStatus?.hls}
              width={'100%'}
              height={'85%'}
              controls
              onPlay={() => {
                track('LIVESTREAM_PLAY');
              }}
              onPause={() => {
                track('LIVESTREAM_PAUSE');
              }}
              light={createDisplayLink(selectedPresentationLivestreamSlot.meetingroom.thumbnail, false)}
            />
          </div>
          <div className="w-full max-w-full sm:max-h-full bg-white sm:w-1/3 h-[80vh] max-h-[40%]">
            <PresentationLivestreamChat meeting={selectedPresentationLivestreamSlot?.meetingroom} />
          </div>
        </div>
      )}
    </VyModal>
  );
};
